import type { Device } from '@studio/models/device';
import { useLazyRef, useUnmount, χref } from '@studio/react-utils/hooks';
import { toJson } from '@tryiris-ai/js-types';
import { Ambits, DefaultRate } from './constants.js';
// Import { Easer } from './easer.js';
import { Pipeline } from './pipeline.js';
import { Piston } from './piston.js';
import { Throttle } from './throttle.js';
import type { Rate, Transform } from './types.js';
import { Direction, Facet, Tempo } from './types.js';
import { getFasterPace, getSlowerPace, toRate, toTempo } from './utils.js';
import { locateProxy } from '#~/agent/index.js';
import { useListener as useListener_ } from '#~/command/hooks.js';
import { Command } from '#~/command/index.js';
import type { UsePrefState } from '#~/preferences/hooks.js';
import { usePrefState } from '#~/preferences/hooks.js';

/**
 * Indicates if easing is enabled value.
 *
 * @param deviceId - Device ID.
 * @param facet - Control facet.
 * @param direction - Ramp direction.
 * @returns `true` if easing is enabled in the specified direction, `false` otherwise.
 */
export const useEasingPref = (
  deviceId: Device['id'],
  facet: Facet,
  direction: Direction = Direction.Up
): UsePrefState<boolean> => usePrefState(`control.${deviceId}.easing.${facet}.${direction}`, true);

/**
 * Command listener.
 *
 * @param deviceId - Device ID.
 */
export const useListener = (deviceId: Device['id']): void => {
  const [panTiltRate, setPanTiltRate] = useRatePref(deviceId, Facet.PanTilt);
  const [panTiltTempo, setPanTiltTempo] = useTempoPref(deviceId, Facet.PanTilt);
  const [zoomRate, setZoomRate] = useRatePref(deviceId, Facet.Zoom);

  useListener_(({ command }) => {
    if (command === Command.PACE_PT_UP) {
      setPanTiltRate({ ...panTiltRate, pace: getFasterPace(panTiltRate.pace) });
    } else if (command === Command.PACE_PT_DOWN) {
      setPanTiltRate({ ...panTiltRate, pace: getSlowerPace(panTiltRate.pace) });
    } else if (command === Command.PACE_ZOOM_UP) {
      setZoomRate({ ...zoomRate, pace: getFasterPace(zoomRate.pace) });
    } else if (command === Command.PACE_ZOOM_DOWN) {
      setZoomRate({ ...zoomRate, pace: getSlowerPace(zoomRate.pace) });
    } else if (command === Command.TEMPO_PT_TOGGLE) {
      setPanTiltTempo(panTiltTempo === Tempo.Fixed ? Tempo.Variable : Tempo.Fixed);
    }
  });
};

/**
 * Use a managed pipeline.
 *
 * @param deviceId - Device ID.
 * @param transforms - Additional pipeline transforms (non-reactive).
 * @returns Movement pipeline.
 */
export const usePipeline = (_deviceId: Device['id'], ...transforms: Transform[]): Pipeline => {
  // Create piston
  const ref = useLazyRef(() => {
    /*
     *Const easers = {
     *  panTilt: new Easer(Ambits[Facet.PanTilt]),
     *  zoom: new Easer(Ambits[Facet.Zoom])
     *};
     */

    const throttles = {
      panTilt: new Throttle(Ambits[Facet.PanTilt]),
      zoom: new Throttle(Ambits[Facet.Zoom])
    };

    // Easing before throttle!
    const pipeline = new Pipeline(
      // ...Object.values(easers),
      ...Object.values(throttles),
      ...transforms
    );

    return {
      // Easers,
      pipeline,
      throttles
    };
  });

  return χref(ref).pipeline;
};

/**
 * Use a managed piston.
 *
 * @param deviceId - Device ID.
 * @param transforms - Additional pipeline transforms (non-reactive).
 * @returns Movement piston.
 */
export const usePiston = (deviceId: Device['id'], ...transforms: Transform[]): Piston => {
  const pipeline = usePipeline(deviceId, ...transforms);

  const agent = locateProxy(deviceId);
  const joystickUpdateFrequency =
    agent?.state.status?.device[deviceId]?.config?.joystickUpdateFrequency;

  // Create piston
  const piston = useLazyRef(() => new Piston(pipeline, joystickUpdateFrequency ?? 10)).current;

  // Stop on piston unmount.
  useUnmount((): void => {
    piston.stop();
  });

  return piston;
};

/**
 * Movement rate for the specified axis.
 *
 * @param deviceId - Device ID.
 * @param facet - Control facet.
 * @returns Movement rate for the specified control facet.
 */
export function useRatePref(deviceId: Device['id'], facet: Facet): UsePrefState<Rate> {
  return usePrefState(`control.${deviceId}.rate.${facet}`, DefaultRate, {
    deserialize: toRate,
    serialize: toJson
  });
}

/**
 * PTZ movement tempo (fixed/variable).
 *
 * @param deviceId - Device ID.
 * @param facet - Control facet.
 * @returns PTZ movement tempo for the specified control facet.
 */
export function useTempoPref(deviceId: Device['id'], facet: Facet): UsePrefState<Tempo> {
  return usePrefState(`control.${deviceId}.tempo.${facet}`, Tempo.Variable, {
    deserialize: toTempo
  });
}
